import { getScrollEventTarget, getScrollTop } from '@theme/scroll-container';

const MOBILE_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DEFAULT_SCROLL_FACTOR = 0.25;
const DEFAULT_PIXELS_PER_SECOND = 59;

class SybilMarquee extends HTMLElement {
  #track;
  #copy;
  #scrollTarget;
  #mobileQuery;
  #motionQuery;
  #controller;
  #resizeObserver;
  #rafId = 0;
  #copyWidth = 0;
  #scrollListening = false;

  connectedCallback() {
    this.#track = this.querySelector('[data-testid="sybil-marquee-track"]');
    this.#copy = this.querySelector('[data-testid="sybil-marquee-copy"]');

    if (!this.#track || !this.#copy) return;

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#scrollTarget = getScrollEventTarget();
    this.#controller = new AbortController();
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.#copy);

    window.addEventListener('resize', this.#handleResize, {
      passive: true,
      signal: this.#controller.signal,
    });
    this.#mobileQuery.addEventListener('change', this.#handleMediaChange, {
      signal: this.#controller.signal,
    });
    this.#motionQuery.addEventListener('change', this.#handleMediaChange, {
      signal: this.#controller.signal,
    });

    this.#syncMotionMode();
  }

  disconnectedCallback() {
    this.#removeScrollListener();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }

    this.#track?.style.removeProperty('transform');
    this.style.removeProperty('--sybil-marquee-duration');
    delete this.dataset.motionReady;
    this.#copyWidth = 0;
  }

  #handleScroll = () => {
    if (!this.#usesScrollLinkedMotion() || this.#rafId) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      if (this.#usesScrollLinkedMotion()) this.#updateScrollPosition();
    });
  };

  #handleResize = () => {
    this.#measureCopyWidth();
    this.#syncScrollTarget();

    if (this.#usesScrollLinkedMotion()) {
      this.#updateScrollPosition();
    } else {
      this.#track?.style.removeProperty('transform');
    }
  };

  #handleMediaChange = () => {
    this.#syncMotionMode();
  };

  #syncScrollTarget() {
    const nextScrollTarget = getScrollEventTarget();
    if (nextScrollTarget !== this.#scrollTarget) {
      this.#removeScrollListener();
      this.#scrollTarget = nextScrollTarget;
    }

    if (!this.#usesScrollLinkedMotion() || this.#scrollListening) return;

    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#controller.signal,
    });
    this.#scrollListening = true;
  }

  #removeScrollListener() {
    if (!this.#scrollListening) return;
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollListening = false;
  }

  #usesScrollLinkedMotion() {
    return this.#mobileQuery?.matches && !this.#motionQuery?.matches;
  }

  #syncMotionMode() {
    this.#measureCopyWidth();
    this.#syncScrollTarget();

    if (this.#usesScrollLinkedMotion()) {
      this.#updateScrollPosition();
    } else {
      this.#track.style.removeProperty('transform');
    }
  }

  #measureCopyWidth = () => {
    this.#copyWidth = this.#copy.getBoundingClientRect().width;
    this.#syncAnimationDuration();
  };

  #syncAnimationDuration() {
    if (!this.#copyWidth) return;

    const configuredSpeed = Number(this.dataset.pixelsPerSecond);
    const pixelsPerSecond =
      Number.isFinite(configuredSpeed) && configuredSpeed > 0
        ? configuredSpeed
        : DEFAULT_PIXELS_PER_SECOND;
    const duration = this.#copyWidth / pixelsPerSecond;

    this.style.setProperty('--sybil-marquee-duration', `${duration}s`);
    this.dataset.motionReady = 'true';
  }

  #updateScrollPosition() {
    const copyWidth = this.#copyWidth;
    if (!copyWidth) return;

    const scrollFactor = Number(this.dataset.scrollFactor) || DEFAULT_SCROLL_FACTOR;
    const scrollDistance = (Math.max(0, getScrollTop()) * scrollFactor) % copyWidth;
    const direction = this.classList.contains('sybil-marquee--right') ? 1 : -1;

    this.#track.style.transform = `translate3d(${scrollDistance * direction}px, 0, 0)`;
  }
}

if (!customElements.get('sybil-marquee')) {
  customElements.define('sybil-marquee', SybilMarquee);
}
